import { Component, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { OfficesList } from "../../components/offices-list/offices-list";
import { Router } from '@angular/router';
import { Routes } from 'src/app/core/routes/routes';
import { PaginatorState } from 'primeng/paginator';
import { OfficeApi } from '@features/business/services/office-api';
import { BusinessApi } from '@features/business/services/business-api';
import { DataEndpoint } from '@core/models/general.model';
import { OfficeFilterModel, OfficeListModel, OfficePaginatedRequestModel } from '@features/business/models/office.model';
import { BusinessGroupedModel } from '@features/business/models/business.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmationDialog } from "@shared/components/confirmation-dialog/confirmation-dialog";
import { OfficeFilter } from "../../components/office-filter/office-filter";
import { Country } from '@shared/models/location.model';
import { combineLatest } from 'rxjs';
import { CountryService } from '@shared/services/country-service/country-service';
import { BlockUI } from "primeng/blockui";
import { ProgressSpinner } from "primeng/progressspinner";
import { HasPermissionPipe } from '@shared/pipes/has-permission.pipe';
import { UserPermission } from '@core/auth/services/auth-service';

@Component({
  selector: 'app-offices',
  imports: [
    ButtonModule,
    OfficesList,
    OfficeFilter,
    ToastModule,
    ConfirmationDialog,
    BlockUI,
    ProgressSpinner,
    HasPermissionPipe
],
  templateUrl: './offices.html',
  styleUrl: './offices.scss',
  providers: [MessageService, ConfirmationService]
})
export class Offices {
  private router = inject(Router);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private countryService = inject(CountryService);
  private officeApi = inject(OfficeApi);
  private businessApi = inject(BusinessApi);

  UserPermission = UserPermission;
  data: OfficeListModel[] = [];
  filteredData: OfficeListModel[] = [];
  businessGroupOptions: BusinessGroupedModel[] = [];
  isLoading: boolean = false;
  sortBy: string | null = null;
  sortOrder: 'asc' | 'desc' | null = null;
  currentFilters = signal<OfficeFilterModel>({});

  // Estado de paginación
  paginationState = signal<PaginatorState>({
    first: 0,
    rows: 10,
    page: 0
  });
  totalRecords: number = 0;

  // Variables para gestionar activar/inactivar sede
  showStatusChangeDialog: boolean = false;
  selectedOffice: OfficeListModel | null = null;
  changeStatusMessage: string = '';
  changeStatusButton: string = '';

  ngOnInit() {
    this.loadData();
    this.getBusinessGroupList();
  }

  loadData() {
    this.isLoading = true;

    const request: OfficePaginatedRequestModel = {
      pageNumber: (this.paginationState().page ?? 0) + 1,
      pageSize: this.paginationState().rows,
      searchTerm: this.currentFilters().searchText,
      businessId: this.currentFilters().selectedBusinesses,
      sortBy: this.sortBy ? this.columnKeyMap[this.sortBy] : undefined,
      sortOrder: this.sortOrder ?? undefined
    }

    // combineLatest garantiza que el procesamiento continúe solo cuando
    // ambas respuestas estén disponibles.
    combineLatest([
      this.officeApi.getOfficeList(request),
      this.countryService.getCountries()
    ]).subscribe({
      next: ([response, countries]) => {
        if (response.Status === 200) {
          const data = response.Data?.records ?? [];

          const records = data.map(office => ({
            ...office,
            Location: {
              ...office.Location,
              CountryIso2: this.resolveCountryIso2(
                office.Location.Country,
                countries
              )
            }
          }));

          this.data = records;
          this.filteredData = records;
          this.totalRecords = response.Data?.pagination.totalCount ?? 0;
        }

        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message
        });
      }
    });
  }

  private resolveCountryIso2(
    countryName: string,
    countries: Country[]
  ): string | undefined {
    return countries.find(
      c => c.name.toLowerCase() === countryName.toLowerCase()
    )?.iso2;
  }

  onPageChange(state: PaginatorState) {
    this.paginationState.set(state);
    this.loadData();
  }

  getBusinessGroupList() {
    this.isLoading = true;

    this.businessApi.getBusinessGrouped().subscribe({
      next: (response: DataEndpoint<BusinessGroupedModel[]>) => {
        if (response.Status === 200) {
          this.businessGroupOptions = response.Data ?? [];
          this.isLoading = false;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message });
      }
    })
  }

  handleConfirmChangeStatus(office: OfficeListModel) {
    this.selectedOffice = office;

    // Si la sede va a ser desactivada
    if (office.Status === 'ENABLED') {
      this.changeStatusMessage = `¿Está seguro de que desea desactivar la sede ${office.Name}?`;
      this.changeStatusButton = 'Desactivar';
    }
    // Si la sede va a ser activada
    else {
      this.changeStatusMessage = `¿Está seguro de que desea activar la sede ${office.Name}?`;
      this.changeStatusButton = 'Activar';
    }

    this.showStatusChangeDialog = true;
  }

  confirmStatusChange() {
    if (this.selectedOffice) {
      this.changeOfficeStatus(this.selectedOffice.Id);
    }
    this.showStatusChangeDialog = false;
    this.selectedOffice = null;
  }

  cancelStatusChange() {
    this.showStatusChangeDialog = false;
    this.selectedOffice = null;
  }

  changeOfficeStatus(officeId: string) {
    this.isLoading = true;

    this.officeApi.updateOfficeStatus(officeId).subscribe({
      next: (response: DataEndpoint<any>) => {
        if (response.Success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Acción exitosa',
            detail: 'El estado ha sido actualizado satisfactoriamente.'
          });
          this.loadData();
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message });
      }
    })
  }

  onCreateOffice() {
    this.router.navigate([Routes.officesAddPage])
  }

  onBulkUpload() {
    this.router.navigate([Routes.officesBulkUploadPage]);
  }

  onFilterChange(filters: OfficeFilterModel) {
    this.currentFilters.set(filters);

    // Resetear a la primera página al filtrar
    this.paginationState.set({
      first: 0,
      rows: this.paginationState().rows,
      page: 0
    });

    this.loadData();
  }

  onClearFilters(): void {
    this.currentFilters.set({});

    // Resetear a la primera página al limpiar
    this.paginationState.set({
      first: 0,
      rows: this.paginationState().rows,
      page: 0
    });

    this.loadData();
  }

  // Configura las columnas con sort
  private columnKeyMap: Record<string, string> = {
    BusinessName: 'BusinessName',
    Name: 'Name',
    Address: 'Address',
    Location: 'Location'
  };

  // Toggle de estados: null -> asc -> desc -> null
  toggleSort(columnKey: string) {
    if (!this.columnKeyMap[columnKey]) return;

    if (this.sortBy !== columnKey) {
      // Nueva columna -> iniciar asc
      this.sortBy = columnKey;
      this.sortOrder = 'asc';
    } else {
      // Misma columna -> avanzar el ciclo
      if (this.sortOrder === 'asc') this.sortOrder = 'desc';
      else if (this.sortOrder === 'desc') {
        // Quitar sort
        this.sortBy = null;
        this.sortOrder = null;
      } else {
        this.sortOrder = 'asc';
      }
    }

    // Resetear la paginación al aplicar un nuevo orden
    this.paginationState().page = 0;
    this.loadData();
  }
}
